'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import VolunteerApplicationModal from './VolunteerApplicationModal';
import { submitVolunteerApplication } from '@/lib/services/volunteerApplicationService';
import { VolunteerApplicationData } from './VolunteerApplicationModal';

interface Opportunity {
  id: number;
  title: string;
  location: string;
  duration: string;
  commitment: string;
  skills: string[];
  icon: string;
  urgency: 'high' | 'medium' | 'low';
}

const VolunteerSection = () => {
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const opportunities: Opportunity[] = [
    {
      id: 1,
      title: 'Emergency Response Coordinator',
      location: 'Multiple Locations',
      duration: '3-6 months',
      commitment: 'Full-time',
      skills: ['Crisis Management', 'Logistics', 'Team Leadership'],
      icon: 'ExclamationTriangleIcon',
      urgency: 'high'
    },
    {
      id: 2,
      title: 'Medical Professional',
      location: 'Field Clinics',
      duration: '2-4 weeks',
      commitment: 'Full-time',
      skills: ['Healthcare', 'Emergency Medicine', 'Cultural Sensitivity'],
      icon: 'HeartIcon',
      urgency: 'high'
    },
    {
      id: 3,
      title: 'Education Program Facilitator',
      location: 'Refugee Camps',
      duration: '6-12 months',
      commitment: 'Full-time',
      skills: ['Teaching', 'Curriculum Development', 'Child Psychology'],
      icon: 'AcademicCapIcon',
      urgency: 'medium'
    },
    {
      id: 4,
      title: 'Water & Sanitation Engineer',
      location: 'Rural Communities',
      duration: '4-8 weeks',
      commitment: 'Full-time',
      skills: ['Civil Engineering', 'Project Management', 'Community Engagement'],
      icon: 'WrenchScrewdriverIcon',
      urgency: 'medium'
    },
    {
      id: 5,
      title: 'Community Organizer',
      location: 'Urban Centers',
      duration: 'Ongoing',
      commitment: 'Part-time',
      skills: ['Community Building', 'Event Planning', 'Communication'],
      icon: 'UserGroupIcon',
      urgency: 'low'
    },
    {
      id: 6,
      title: 'Digital Content Creator',
      location: 'Remote',
      duration: 'Flexible',
      commitment: 'Part-time',
      skills: ['Photography', 'Videography', 'Storytelling'],
      icon: 'CameraIcon',
      urgency: 'low'
    }
  ];

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'high':
        return 'bg-error text-error-foreground';
      case 'medium':
        return 'bg-warning text-warning-foreground';
      case 'low':
        return 'bg-success text-success-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleApplyClick = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOpportunity(null);
  };

  const handleSubmitApplication = async (data: VolunteerApplicationData) => {
    const response = await submitVolunteerApplication(data);
    if (!response.success) {
      throw new Error(response.message);
    }
  };

  return (
    <section id="volunteer-opportunities" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-headline font-bold text-foreground mb-4">
            Volunteer Opportunities
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Join our global network of compassionate volunteers making direct impact on the ground. Your skills, time, and dedication can transform lives.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {opportunities.map((opportunity) => (
            <div
              key={opportunity.id}
              className="bg-card rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 border border-border"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-14 h-14 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={opportunity.icon as any} size={28} className="text-primary" variant="solid" />
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-cta font-bold ${getUrgencyColor(opportunity.urgency)}`}>
                  {opportunity.urgency.toUpperCase()}
                </span>
              </div>

              <h3 className="text-xl font-headline font-bold text-foreground mb-3">
                {opportunity.title}
              </h3>

              <div className="space-y-2 mb-4">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="MapPinIcon" size={16} className="text-primary" variant="solid" />
                  <span className="font-body">{opportunity.location}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="ClockIcon" size={16} className="text-primary" variant="solid" />
                  <span className="font-body">{opportunity.duration}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="CalendarIcon" size={16} className="text-primary" variant="solid" />
                  <span className="font-body">{opportunity.commitment}</span>
                </div>
              </div>

              <div className="mb-4">
                <div className="text-sm font-cta font-bold text-foreground mb-2">
                  Required Skills:
                </div>
                <div className="flex flex-wrap gap-2">
                  {opportunity.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-muted text-foreground rounded-full text-xs font-body"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <button 
                onClick={() => handleApplyClick(opportunity)}
                className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-lg font-cta font-bold text-sm transition-all duration-300 hover:bg-primary/90 flex items-center justify-center space-x-2"
              >
                <Icon name="HandRaisedIcon" size={18} variant="solid" />
                <span>Apply Now</span>
              </button>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-8 border border-primary/20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-headline font-bold text-foreground mb-4">
                Can&apos;t Volunteer On-Ground?
              </h3>
              <p className="text-lg text-muted-foreground font-body mb-6">
                Support our mission remotely through virtual volunteering opportunities. Contribute your professional skills from anywhere in the world.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start space-x-3">
                  <Icon name="CheckCircleIcon" size={20} className="text-success mt-1" variant="solid" />
                  <span className="text-foreground font-body">
                    Remote project management and coordination
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <Icon name="CheckCircleIcon" size={20} className="text-success mt-1" variant="solid" />
                  <span className="text-foreground font-body">
                    Digital marketing and social media advocacy
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <Icon name="CheckCircleIcon" size={20} className="text-success mt-1" variant="solid" />
                  <span className="text-foreground font-body">
                    Translation and content localization services
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <Icon name="CheckCircleIcon" size={20} className="text-success mt-1" variant="solid" />
                  <span className="text-foreground font-body">
                    Data analysis and impact measurement support
                  </span>
                </li>
              </ul>
              <button className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-cta font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-lg flex items-center space-x-2">
                <Icon name="ComputerDesktopIcon" size={24} variant="solid" />
                <span>Explore Remote Opportunities</span>
              </button>
            </div>

            <div className="bg-card rounded-xl p-6 shadow-lg border border-border">
              <h4 className="text-xl font-headline font-bold text-foreground mb-4">
                Volunteer Benefits
              </h4>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="AcademicCapIcon" size={20} className="text-primary" variant="solid" />
                  </div>
                  <div>
                    <div className="font-cta font-bold text-foreground mb-1">
                      Professional Development
                    </div>
                    <p className="text-sm text-muted-foreground font-body">
                      Gain valuable experience in humanitarian work
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="UserGroupIcon" size={20} className="text-primary" variant="solid" />
                  </div>
                  <div>
                    <div className="font-cta font-bold text-foreground mb-1">
                      Global Network
                    </div>
                    <p className="text-sm text-muted-foreground font-body">
                      Connect with like-minded changemakers worldwide
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="HeartIcon" size={20} className="text-primary" variant="solid" />
                  </div>
                  <div>
                    <div className="font-cta font-bold text-foreground mb-1">
                      Direct Impact
                    </div>
                    <p className="text-sm text-muted-foreground font-body">
                      Witness firsthand the transformation you create
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="DocumentTextIcon" size={20} className="text-primary" variant="solid" />
                  </div>
                  <div>
                    <div className="font-cta font-bold text-foreground mb-1">
                      Certification
                    </div>
                    <p className="text-sm text-muted-foreground font-body">
                      Receive official recognition for your service
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Volunteer Application Modal */}
      {selectedOpportunity && (
        <VolunteerApplicationModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          opportunityTitle={selectedOpportunity.title}
          opportunityId={selectedOpportunity.id}
          onSubmit={handleSubmitApplication}
        />
      )}
    </section>
  );
};

export default VolunteerSection;